/**
 * ClosePlayerModal component.
 */
import Buttons from '@components/elements/modal/genericAcceptModalButtons';
import Container from '@components/atoms/container/modalContainer';
import FlavourConfig from '@shared/config/flavours';
import Focus from '@shared/types/enums/focus';
import Title from '@components/atoms/text/modalTitle';
import TranslationManager from '@shared/assets/locale/translationsManager';
import { channelDown, channelUp } from '@shared/store/thunks/channels.thunk';
import { mediaSelector } from '@shared/store/slices/media.slice';
import { modalSelector } from '@shared/store/slices/modal.slice';
import { popRoute } from '@shared/store/thunks/route.thunk';
import { setFocus } from '@shared/store/slices/focus.slice';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useEffect, useRef, useState } from 'react';

/**
 * Constants.
 *
 * @constant {number} TIME_TO_REFOCUS - The time to refocus the modal buttons.
 */
const TIME_TO_REFOCUS = 3000;

/**
 * Displays the close player modal.
 */
export default function ClosePlayerModal() {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  // Dispatch
  const dispatch = useAppDispatch();

  // Selectors
  const { live } = useAppSelector(mediaSelector);
  const { params } = useAppSelector(modalSelector);

  // States
  const [callback, setCallback] = useState<() => void>(() => null);

  // Refs
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  // ------------------------------------------------------------
  // Effect hooks
  // ------------------------------------------------------------

  /* Workaround in case focus doesn't set correctly in LG monitors */
  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      dispatch(setFocus(Focus.MODAL_BUTTONS));
    }, TIME_TO_REFOCUS);
    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const { key } = params || {};
    switch (key) {
      case 'CHANNEL_UP':
      case 'UP':
        setCallback(channelUp);
        break;
      case 'CHANNEL_DOWN':
      case 'DOWN':
        setCallback(channelDown);
        break;
      default:
        setCallback(popRoute);
        break;
    }
  }, [params]);

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------

  return (
    <Container id={'closePlayerModalContainer'}>
      <Title>
        {TranslationManager.get(
          'MODAL.CLOSE_PLAYER.TITLE',
          FlavourConfig.appTitle
        )}
      </Title>
      <Buttons
        acceptAction={() => dispatch(callback)}
        cancelAction={() =>
          dispatch(setFocus(live ? Focus.ZAPPER : Focus.PLAYER))
        }
      />
    </Container>
  );
}
